import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  runGmailDeletion,
  saveAgreementGmailDeletion,
} from '../../../api/users-api';
import { handleErrors } from '../../../utils/errors';
import { GmailDeletionModel } from '../../../../types';
import SharedDialog from '../../../shared/shared-dialog';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  ToolsRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import SharedFilePickerOld from '../../../shared/shared-file-picker-old';
import SharedFilePicker from '../../../shared/shared-file-picker';
import { useUserSettings } from '../../../utils/use-user-settings';
import { isFeatureEnabled } from '../../../utils/feature-flags';

import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

interface GmailDeletionProcessDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedModel: GmailDeletionModel;
  loggedInUser: string;
  changeLogRoles: string[];
  accessToken: string;
}

export default function GmailDeletionProcessDialog({
  open,
  handleClose,
  selectedModel,
  loggedInUser,
  changeLogRoles,
}: GmailDeletionProcessDialogProps) {
  const { createEvent } = useAnalytics();
  const { features } = useUserSettings();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [spreadSheetName, setSpreadSheetName] = useState('');
  const [spreadSheetID, setSpreadSheetID] = useState('');
  const [userConfirm, setUserConfirm] = useState('');
  const [hasError, setHasError] = useState(false);

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      runGmailDeletion(selectedModel, spreadSheetID, changeLogMessage),
    onSuccess: (data) => {
      window.open(
        `https://docs.google.com/a/${
          loggedInUser.split('@')[1]
        }/spreadsheet/ccc?key=${data}#gid=0`,
        '_blank',
        'noopener, noreferrer'
      );
      setSpreadSheetName('');
      setSpreadSheetID('');
      setHasError(false);
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue performing this gmail deletion job. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.import_export.gmail_deletion.run_gmail_deletion, {
        module: ModuleList.modules.importExport.name,
        section: ModuleList.modules.importExport.sections.gmailDeletion,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (userConfirm.toLocaleLowerCase().trim() !== 'all mail') {
      setHasError(true);
    } else if (
      isAbleToSeeUIElement(changeLogRoles, ToolsRoles.RunGmailSearch.value)
    ) {
      setHasError(false);
      setOpenChangeLogDialog(true);
    } else {
      setHasError(false);
      saveAgreementGmailDeletion(selectedModel);
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={() => {
        setSpreadSheetName('');
        setSpreadSheetID('');
        setHasError(false);
        handleClose();
      }}
      title="Run Gmail Deletion"
      dialogContent={
        <Stack gap={3}>
          <Typography variant="body2">
            Please select the Gmail Deletion Report you would like to import.
            This process will delete ALL emails represented on the report.
          </Typography>
          {isFeatureEnabled('drivePickerUIReact', features) ? (
            <SharedFilePicker
              pickerType="spreadsheet"
              selectedFileId={spreadSheetID}
              selectedFileName={spreadSheetName}
              onFileSelected={({ id, name }) => {
                setSpreadSheetID(id);
                setSpreadSheetName(name);
              }}
              onFileRemoved={() => {
                setSpreadSheetID('');
                setSpreadSheetName('');
              }}
              includeSharedDrives
            />
          ) : (
            <SharedFilePickerOld
              pickerType="spreadsheet"
              selectedFileId={spreadSheetID}
              selectedFileName={spreadSheetName}
              onFileSelected={({ id, name }) => {
                setSpreadSheetID(id);
                setSpreadSheetName(name);
              }}
              onFileRemoved={() => {
                setSpreadSheetID('');
                setSpreadSheetName('');
              }}
              includeSharedDrives
            />
          )}

          {spreadSheetID.length > 0 ? (
            <Stack gap={2}>
              <Typography variant="body2">
                Now confirm that you wish to delete all Gmail messages/threads
                from the selected spreadsheet by entering 'ALL MAIL' down below:
              </Typography>
              <TextField
                id="confirm"
                label="ALL MAIL"
                error={hasError}
                helperText={
                  hasError ? 'Please enter ALL MAIL to confirm.' : ' '
                }
                color={
                  userConfirm.toLocaleLowerCase().trim() !== 'all mail'
                    ? 'warning'
                    : 'primary'
                }
                fullWidth
                autoComplete="off"
                value={userConfirm}
                onChange={(event) => setUserConfirm(event.target.value)}
              />
            </Stack>
          ) : null}
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={() => {
              saveAgreementGmailDeletion(selectedModel);
              mutate();
            }}
            mutateLoading={isPending}
          />
        </Stack>
      }
      primaryButton={{
        title: 'Run Gmail Deletion',
        action: handleSubmit,
        loading: isPending,
        disabled:
          isPending || userConfirm.toLocaleLowerCase().trim() !== 'all mail',
      }}
    />
  );
}
